import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SATURATIONS, LIGHTNESSES, CURRENT_BRAND_COLOR, PREVIEW_LIGHTNESS_STEPS, HSLColor, hslToHex, hexToHsl, isValidHex, generateDynamicHues, getHueName, normalizeHue } from '../utils/colorUtils';
import { ColorSwatch } from './ColorSwatch';
import { ColorPreview } from './ColorPreview';
import { ColorPickerPopup } from './ColorPickerPopup';
import { Pipette, RotateCcw, Sun, Moon, ChevronLeft, ChevronRight } from 'lucide-react';
const MAX_HISTORY = 15;
export function ColorExplorer() {
  const [selectedColor, setSelectedColor] = useState<HSLColor>(CURRENT_BRAND_COLOR);
  const [hexInput, setHexInput] = useState<string>(hslToHex(CURRENT_BRAND_COLOR.h, CURRENT_BRAND_COLOR.s, CURRENT_BRAND_COLOR.l));
  const [baseColor, setBaseColor] = useState<HSLColor>(CURRENT_BRAND_COLOR);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  // Store the color before opening picker for cancel functionality
  const [prePickerColor, setPrePickerColor] = useState<HSLColor | null>(null);
  // History state for back/forward navigation
  const [colorHistory, setColorHistory] = useState<HSLColor[]>([CURRENT_BRAND_COLOR]);
  const [historyIndex, setHistoryIndex] = useState(0);
  // Hue spread control (default 30°)
  const [hueSpread, setHueSpread] = useState(30);
  // Helper to check if two colors are the same
  const isSameColor = (a: HSLColor, b: HSLColor) => a.h === b.h && a.s === b.s && a.l === b.l;
  // Add color to history (only for committed changes)
  const addToHistory = useCallback((color: HSLColor) => {
    setColorHistory(prev => {
      // Get the current index safely
      const currentIndex = Math.min(historyIndex, prev.length - 1);
      // Don't add if it's the same as current position
      const currentColor = prev[currentIndex];
      if (currentColor && isSameColor(currentColor, color)) {
        return prev;
      }
      // If we're not at the end, truncate forward history
      const newHistory = prev.slice(0, currentIndex + 1);
      newHistory.push(color);
      // Limit to MAX_HISTORY
      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
        return newHistory;
      }
      return newHistory;
    });
    setHistoryIndex(prev => {
      // If we truncated, new index is at the end
      return Math.min(prev + 1, MAX_HISTORY - 1);
    });
  }, [historyIndex]);
  // Navigate back in history
  const handleBack = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const color = colorHistory[newIndex];
      setBaseColor(color);
      setSelectedColor(color);
      setHexInput(hslToHex(color.h, color.s, color.l));
    }
  }, [historyIndex, colorHistory]);
  // Navigate forward in history
  const handleForward = useCallback(() => {
    if (historyIndex < colorHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const color = colorHistory[newIndex];
      setBaseColor(color);
      setSelectedColor(color);
      setHexInput(hslToHex(color.h, color.s, color.l));
    }
  }, [historyIndex, colorHistory]);
  const canGoBack = historyIndex > 0;
  const canGoForward = historyIndex < colorHistory.length - 1;
  // Generate dynamic hues based on base color
  const dynamicHues = useMemo(() => {
    return generateDynamicHues(baseColor.h, hueSpread, 14);
  }, [baseColor.h, hueSpread]);
  // Get hue range description
  const hueRangeDescription = useMemo(() => {
    const startHue = dynamicHues[0];
    const endHue = dynamicHues[dynamicHues.length - 1];
    const startName = getHueName(startHue);
    const endName = getHueName(endHue);
    if (startName === endName) {
      return `${startName} spectrum (${startHue}° → ${endHue}°)`;
    }
    return `${startName} (${startHue}°) → ${endName} (${endHue}°)`;
  }, [dynamicHues]);
  // Initialize theme from document
  useEffect(() => {
    const isDark = document.documentElement.classList.contains('dark');
    setIsDarkMode(isDark);
  }, []);
  // Toggle theme
  const toggleTheme = () => {
    const newIsDark = !isDarkMode;
    setIsDarkMode(newIsDark);
    if (newIsDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };
  // Generate dynamic presets based on base color
  const dynamicPresets = useMemo(() => {
    const {
      h,
      s,
      l
    } = baseColor;
    return [{
      color: {
        h,
        s,
        l
      },
      label: 'Current'
    }, {
      color: {
        h: normalizeHue(h + 180),
        s,
        l
      },
      label: 'Complement'
    }, {
      color: {
        h: normalizeHue(h + 30),
        s,
        l
      },
      label: 'Analogous +'
    }, {
      color: {
        h: normalizeHue(h - 30),
        s,
        l
      },
      label: 'Analogous -'
    }, {
      color: {
        h,
        s: Math.min(100, s + 20),
        l
      },
      label: 'More Vivid'
    }, {
      color: {
        h,
        s: Math.max(0, s - 30),
        l
      },
      label: 'Muted'
    }, {
      color: {
        h,
        s,
        l: Math.min(90, l + 15)
      },
      label: 'Lighter'
    }, {
      color: {
        h,
        s,
        l: Math.max(20, l - 15)
      },
      label: 'Darker'
    }];
  }, [baseColor]);
  // Generate spectrum from base color
  const spectrumColors = useMemo(() => {
    return PREVIEW_LIGHTNESS_STEPS.map(l => ({
      h: baseColor.h,
      s: baseColor.s,
      l
    }));
  }, [baseColor]);
  // Handle hex input change
  const handleHexChange = (value: string) => {
    setHexInput(value);
    if (isValidHex(value)) {
      const hsl = hexToHsl(value);
      if (hsl) {
        setBaseColor(hsl);
        setSelectedColor(hsl);
        addToHistory(hsl);
      }
    }
  };
  // Handle preset click
  const handlePresetClick = (color: HSLColor) => {
    setBaseColor(color);
    setSelectedColor(color);
    setHexInput(hslToHex(color.h, color.s, color.l));
    addToHistory(color);
  };
  // Reset to brand color
  const handleReset = () => {
    setBaseColor(CURRENT_BRAND_COLOR);
    setSelectedColor(CURRENT_BRAND_COLOR);
    setHexInput(hslToHex(CURRENT_BRAND_COLOR.h, CURRENT_BRAND_COLOR.s, CURRENT_BRAND_COLOR.l));
    addToHistory(CURRENT_BRAND_COLOR);
  };
  // Real-time color picker updates (no history add - live preview only)
  const handleColorPickerChange = useCallback((color: HSLColor) => {
    setBaseColor(color);
    setSelectedColor(color);
    setHexInput(hslToHex(color.h, color.s, color.l));
  }, []);
  const handleColorPickerApply = (color: HSLColor) => {
    setBaseColor(color);
    setSelectedColor(color);
    setHexInput(hslToHex(color.h, color.s, color.l));
    setShowColorPicker(false);
    setPrePickerColor(null);
    addToHistory(color);
  };
  const handleColorPickerClose = () => {
    // Revert to pre-picker color if cancelled
    if (prePickerColor) {
      setBaseColor(prePickerColor);
      setSelectedColor(prePickerColor);
      setHexInput(hslToHex(prePickerColor.h, prePickerColor.s, prePickerColor.l));
    }
    setShowColorPicker(false);
    setPrePickerColor(null);
  };
  const handleOpenColorPicker = () => {
    setPrePickerColor(baseColor); // Store current color for cancel
    setShowColorPicker(true);
  };
  const isValidInput = isValidHex(hexInput);
  return <div className="min-h-screen bg-background text-foreground flex flex-col md:flex-row" data-id="element-2">
      {/* Main Content Area */}
      <div className="flex-1 p-4 md:p-8 md:pr-[500px] overflow-x-hidden" data-id="element-3">
        {/* Header */}
        <header className="mb-8 max-w-4xl" data-id="element-4">
          <div className="flex items-center justify-between mb-2" data-id="element-5">
            <h1 className="text-4xl font-bold tracking-tight" data-id="element-6">
              <span style={{
              color: `hsl(${baseColor.h}, ${baseColor.s}%, ${baseColor.l}%)`
            }} data-id="element-7">
                Color
              </span>
              <span className="text-foreground" data-id="element-8">Scale</span>
              <span className="text-muted-foreground" data-id="element-9">.dev</span>
            </h1>

            {/* Theme Toggle - pinned far right */}
            <button onClick={toggleTheme} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors border border-[hsl(var(--border)/0.1)] focus:outline-none focus:ring-2 focus:ring-primary/50 ml-auto" aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-10">
              {isDarkMode ? <>
                  <Sun className="w-4 h-4" data-id="element-11" />
                  <span className="text-sm font-medium" data-id="element-12">Light</span>
                </> : <>
                  <Moon className="w-4 h-4" data-id="element-13" />
                  <span className="text-sm font-medium" data-id="element-14">Dark</span>
                </>}
            </button>
          </div>

          <p className="text-muted-foreground text-lg mb-6" data-id="element-15">
            Enter a hex color to generate the full spectrum
          </p>

          {/* Hex Input Section */}
          <div className="space-y-4" data-id="element-16">
            <div className="flex flex-wrap items-center gap-4 p-4 rounded-xl border border-[hsl(var(--border)/0.2)] bg-card/50 backdrop-blur-sm" data-id="element-17">
              <div className="flex items-center gap-3" data-id="element-18">
                <Pipette className="w-5 h-5 text-muted-foreground" data-id="element-19" />
                <label className="text-sm font-medium text-muted-foreground" data-id="element-20">
                  Base Color:
                </label>
              </div>

              <div className="flex items-center gap-2 relative z-[100]" data-id="element-21">
                <div className="relative" data-id="element-22">
                  <button onClick={handleOpenColorPicker} className="w-10 h-10 rounded-lg shadow-sm border border-[hsl(var(--border)/0.1)] hover:scale-105 transition-transform cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50" style={{
                  backgroundColor: isValidInput ? hexInput : `hsl(${baseColor.h}, ${baseColor.s}%, ${baseColor.l}%)`
                }} aria-label="Open color picker" data-id="element-23" />
                  {showColorPicker && <ColorPickerPopup initialColor={baseColor} onChange={handleColorPickerChange} onApply={handleColorPickerApply} onClose={handleColorPickerClose} data-id="element-24" />}
                </div>
                <input type="text" value={hexInput} onChange={e => handleHexChange(e.target.value)} placeholder="#D6FE50" className={`
                    w-28 px-3 py-2 rounded-lg text-sm font-mono
                    bg-background border border-input
                    focus:outline-none focus:ring-2 focus:ring-primary/50
                    ${!isValidInput && hexInput.length > 0 ? 'border-destructive' : ''}
                  `} data-id="element-25" />
              </div>

              {isValidInput && <code className="text-xs bg-muted px-2 py-1 rounded font-mono" data-id="element-26">
                  HSL({baseColor.h}°, {baseColor.s}%, {baseColor.l}%)
                </code>}

              <button onClick={handleReset} className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-id="element-27">
                <RotateCcw className="w-4 h-4" data-id="element-28" />
                Reset
              </button>

              {/* History Navigation */}
              <div className="flex items-center gap-1 ml-2 border-l border-[hsl(var(--border)/0.1)] pl-4" data-id="element-29">
                <button onClick={handleBack} disabled={!canGoBack} className={`
                    flex items-center gap-1 px-2 py-2 rounded-md text-sm transition-colors
                    ${canGoBack ? 'text-muted-foreground hover:text-foreground hover:bg-secondary' : 'text-muted-foreground/30 cursor-not-allowed'}
                  `} aria-label="Go back to previous color" title={canGoBack ? `Back (${historyIndex} of ${colorHistory.length})` : 'No previous colors'} data-id="element-30">
                  <ChevronLeft className="w-4 h-4" data-id="element-31" />
                </button>
                <span className="text-xs text-muted-foreground tabular-nums min-w-[3ch] text-center" data-id="element-32">
                  {historyIndex + 1}/{colorHistory.length}
                </span>
                <button onClick={handleForward} disabled={!canGoForward} className={`
                    flex items-center gap-1 px-2 py-2 rounded-md text-sm transition-colors
                    ${canGoForward ? 'text-muted-foreground hover:text-foreground hover:bg-secondary' : 'text-muted-foreground/30 cursor-not-allowed'}
                  `} aria-label="Go forward to next color" title={canGoForward ? 'Forward' : 'No forward colors'} data-id="element-33">
                  <ChevronRight className="w-4 h-4" data-id="element-34" />
                </button>
              </div>
            </div>

            {/* Dynamic Quick Presets */}
            <div className="flex flex-wrap items-center gap-2" data-id="element-35">
              <span className="text-xs text-muted-foreground" data-id="element-36">
                Quick experiments:
              </span>
              {dynamicPresets.map(({
              color,
              label
            }) => {
              const isCurrent = color.h === baseColor.h && color.s === baseColor.s && color.l === baseColor.l;
              return <button key={label} onClick={() => handlePresetClick(color)} className={`
                      flex items-center gap-1.5 px-2 py-1 rounded-md text-xs 
                      transition-colors border
                      ${isCurrent ? 'bg-primary/20 border-primary/40 text-foreground' : 'bg-secondary hover:bg-secondary/80 border-[hsl(var(--border)/0.1)]'}
                    `} data-id="element-37">
                    <div className="w-3 h-3 rounded-sm border border-[hsl(var(--border)/0.1)]" style={{
                  backgroundColor: `hsl(${color.h}, ${color.s}%, ${color.l}%)`
                }} data-id="element-38" />
                    {label}
                  </button>;
            })}
            </div>
          </div>
        </header>

        {/* Generated Spectrum Section */}
        <section className="mb-12" data-id="element-39">
          <div className="flex items-baseline gap-4 border-b border-[hsl(var(--border)/0.1)] pb-2 mb-6" data-id="element-40">
            <h2 className="text-2xl font-bold" data-id="element-41">Generated Spectrum</h2>
            <span className="text-sm text-muted-foreground" data-id="element-42">
              Lightness variations from 92% to 8%
            </span>
          </div>

          {/* Large spectrum blocks */}
          <div className="grid grid-cols-8 gap-2 mb-6" data-id="element-43">
            {spectrumColors.map((color, i) => {
            const hex = hslToHex(color.h, color.s, color.l);
            const isSelected = selectedColor.h === color.h && selectedColor.s === color.s && selectedColor.l === color.l;
            return <button key={i} onClick={() => setSelectedColor(color)} className={`
                    aspect-square rounded-xl transition-all duration-200
                    hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/50
                    ${isSelected ? 'ring-2 ring-primary scale-105 shadow-xl z-10' : 'shadow-md'}
                  `} style={{
              backgroundColor: `hsl(${color.h}, ${color.s}%, ${color.l}%)`
            }} data-id="element-44">
                  <span className="sr-only" data-id="element-45">{hex}</span>
                </button>;
          })}
          </div>

          {/* Hex values row */}
          <div className="grid grid-cols-8 gap-2 text-center" data-id="element-46">
            {spectrumColors.map((color, i) => {
            const hex = hslToHex(color.h, color.s, color.l);
            return <div key={i} className="text-[10px] font-mono text-muted-foreground" data-id="element-47">
                  {hex}
                </div>;
          })}
          </div>
        </section>

        {/* Full Exploration Grid - Now Dynamic! */}
        <section className="mb-12" data-id="element-48">
          <div className="flex items-baseline gap-4 border-b border-[hsl(var(--border)/0.1)] pb-2 mb-6" data-id="element-49">
            <h2 className="text-2xl font-bold" data-id="element-50">Full Exploration</h2>
            <span className="text-sm text-muted-foreground" data-id="element-51">
              {hueRangeDescription}
            </span>

            {/* Subtle spread adjustment */}
            <div className="flex items-center gap-2 ml-auto" data-id="element-52">
              <label className="text-xs text-muted-foreground" data-id="element-53">Spread:</label>
              <input type="range" min="10" max="90" step="5" value={hueSpread} onChange={e => setHueSpread(Number(e.target.value))} className="w-20 h-1 bg-secondary rounded-full appearance-none cursor-pointer accent-primary" data-id="element-54" />
              <span className="text-xs text-muted-foreground tabular-nums w-8" data-id="element-55">
                ±{hueSpread}°
              </span>
            </div>
          </div>

          {/* Color Grids by Lightness - Reordered to start with 50% */}
          <div className="space-y-12" data-id="element-56">
            {/* Reorder lightnesses: start with mid-tones (50), then go lighter, then darker */}
            {(() => {
            const filteredLightnesses = LIGHTNESSES.filter((_, i) => i % 3 === 0);
            // Sort to put 50 first, then values > 50 descending, then values < 50 descending
            const reordered = [...filteredLightnesses].sort((a, b) => {
              // 50 always comes first
              if (a === 50) return -1;
              if (b === 50) return 1;
              // Then values closest to 50
              const distA = Math.abs(a - 50);
              const distB = Math.abs(b - 50);
              return distA - distB;
            });
            return reordered;
          })().map(lightness => <div key={lightness} className="space-y-4" data-id="element-57">
                <h3 className="text-lg font-semibold text-muted-foreground" data-id="element-58">
                  Lightness {lightness}%
                </h3>

                <div className="overflow-x-auto pb-4" data-id="element-59">
                  <div className="min-w-[800px]" data-id="element-60">
                    {/* Grid Header - Dynamic Hues */}
                    <div className="grid grid-cols-[50px_repeat(14,1fr)] gap-1.5 mb-1.5 text-center text-[10px] text-muted-foreground font-mono" data-id="element-61">
                      <div data-id="element-62" />
                      {dynamicHues.map(hue => <div key={hue} data-id="element-63">{hue}°</div>)}
                    </div>

                    {/* Grid Rows - Saturations (show fewer) */}
                    <div className="space-y-1.5" data-id="element-64">
                      {SATURATIONS.filter((_, i) => i % 2 === 0).map(saturation => <div key={`${lightness}-${saturation}`} className="grid grid-cols-[50px_repeat(14,1fr)] gap-1.5 items-center" data-id="element-65">
                            <div className="text-right pr-1 text-[10px] text-muted-foreground font-mono" data-id="element-66">
                              {saturation}%
                            </div>
                            {dynamicHues.map(hue => {
                      const color = {
                        h: hue,
                        s: saturation,
                        l: lightness
                      };
                      const isSelected = selectedColor.h === hue && selectedColor.s === saturation && selectedColor.l === lightness;
                      return <ColorSwatch key={`${hue}-${saturation}-${lightness}`} color={color} isSelected={isSelected} onSelect={setSelectedColor} baseColor={baseColor} data-id="element-67" />;
                    })}
                          </div>)}
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </section>

        <footer className="mt-20 pt-8 border-t border-[hsl(var(--border)/0.1)] text-center text-muted-foreground text-sm" data-id="element-68">
          <p data-id="element-69">ColorScale.dev • Color Exploration Tool</p>
        </footer>
      </div>

      {/* Sticky Preview Panel */}
      <ColorPreview selectedColor={selectedColor} baseColor={baseColor} data-id="element-70" />
    </div>;
}